import React, { useState, useRef } from "react";
import InputValidation from "./InputValidation";
import CalculatorLogic from "./CalculatorLogic";
import Tooltip from "./Tooltip";

export default function CalculatorCard({
  logicType,
  description,
  inputs = [],
  customValidation,
  customValidationByIndex = {},
  img = ""
}) {
  const [inputValues, setInputValues] = useState(Array(inputs.length).fill(""));
  const [result, setResult] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [inputErrors, setInputErrors] = useState(Array(inputs.length).fill(""));
  const infoRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleMouseEnter = () => {
     if (!infoRef.current) return;

  const rect = infoRef.current.getBoundingClientRect();
  let x = rect.left + rect.width;
  let y = rect.top - 50;

  // Clamp to prevent overflow
  const tooltipWidth = 300; // Match maxWidth in Tooltip
  const padding = 10;

  if (x + tooltipWidth + padding > window.innerWidth) {
    x = window.innerWidth - tooltipWidth - padding;
  }

  if (x < padding) x = padding;
  if (y < padding) y = rect.bottom + 10; // Put below the icon if no space above
 
  setTooltipPos({ x, y });
  setShowTooltip(true);
};

  const handleMouseLeave = () => setShowTooltip(false);

  const handleInputChange = (e, index) => {
    const newInputs = [...inputValues];
    newInputs[index] = e.target.value;
    setInputValues(newInputs);

    // Clear error on change
    const newErrors = [...inputErrors];
    newErrors[index] = "";
    setInputErrors(newErrors);
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = [...inputErrors];
    let hasError = false;

    inputValues.forEach((value, index) => {
      const validator = customValidationByIndex[index];
      if (validator) {
        const validationResult = validator(value);
        if (validationResult !== true) {
          newErrors[index] = validationResult;
          hasError = true;
        } else {
          newErrors[index] = "";
        }
      } else {
        const defaultResult = InputValidation([value]);
        if (defaultResult !== true) {
          newErrors[index] = defaultResult[0];
          hasError = true;
        } else {
          newErrors[index] = "";
        }
      }
    });

    setInputErrors(newErrors);
    if (!hasError) {
      const output = CalculatorLogic(inputValues, logicType);
      setResult(output);
    }
  };

  const renderResult = (result) => {
    if (!result || !Array.isArray(result.rows) || result.rows.length === 0) return null;

    const isRowLayout = result.rows.length > 3;
const layoutClass = isRowLayout ? 'flex flex-col flex-wrap gap-3' : 'flex flex-row flex-wrap gap-3';

return (
  <div className="card bg-white shadow-md rounded-xl p-4 mt-4 max-w-xl mx-auto">
    <h2 className="text-2xl font-bold mb-4">Result</h2>

    <div className={layoutClass}>
      {result.rows.map((row, index) => (
        <div
          key={index}
          className="bg-gray-100 p-3 rounded-lg flex flex-col items-start flex-2 min-w-[150px]"
        >
          <div className="text-sm text-gray-500">{row.name}</div>
          <div className="text-lg font-semibold">{row.val}</div>
        </div>
      ))}
    </div>
  </div>
);
  };

  return (
    <div className="relative bg-white/60 card p-6 border rounded-md shadow-md backdrop-blur-sm transition">
      {/* Header */}
      <div className="flex items-center justify-center mb-4 relative">
        <h2 className="text-2xl mr-2">{logicType} Calculator</h2>
       <div
          ref={infoRef}
          className="text-sm text-blue-500 cursor-pointer"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
    if (window.innerWidth <= 768) {
      handleMouseEnter();
      setTimeout(() => setShowTooltip(false), 3000); // auto-hide after 3s
    }
  }}
        >
          ℹ️
        </div>

        {showTooltip && (
          <Tooltip
            position={{
              x: tooltipPos.x,
              y: tooltipPos.y,
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave
            }}
          >
            {description}
            <img
              alt={`Helps find inputs for ${logicType}`}
              src={img}
              className="max-w-[200px] w-full rounded-lg mt-2"
            />
          </Tooltip>
        )}
      </div>

      {/* Inputs */}
      <form onSubmit={handleSubmit}>
        {inputs.map((inputConfig, index) => (
          <div key={index} className="mb-4">
            {inputConfig.label && (
              <label className="block text-gray-700 mb-1">
                {inputConfig.label}
              </label>
            )}
            <input
              type="text"
              value={inputValues[index]}
              onChange={(e) => handleInputChange(e, index)}
              placeholder={inputConfig.placeholder || `Input ${index + 1}`}
              className="border p-2 w-full bg-white/40 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            />
            {inputErrors[index] && (
              <div className="text-red-500 text-sm mt-1">
                {inputErrors[index]}
              </div>
            )}
          </div>
        ))}
        <div className="flex gap-4 mb-6">
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl"
        >
          Calculate
        </button>
        <button 
          type= "reset"
          onClick={() => {
            setInputValues(Array(inputs.length).fill(""));
            setResult(null);
          }} 
          className="bg-gray-400 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-xl"
        >
          Reset
        </button>
        </div>
      </form>

      {renderResult(result)}
    </div>
  );
}
